/**
 * HistoryPanel — Iter 189b — Squarespace-killer feature.
 * ============================================================================
 *
 * Slide-in panel (from the right, above the EditDrawer z-order) showing the
 * last 50 recorded actions. Each row displays:
 *   • an icon matching the action kind (add / remove / edit / reorder)
 *   • the human-readable label ("Add heading", "Reorder blocks", …)
 *   • a relative timestamp ("3s ago", "12m ago")
 *   • the current pointer (arrow) if it's the active snapshot
 *
 * Clicking a row calls `onJumpTo(index)` — flips the entire canvas to that
 * snapshot in one step. Zero downside: the shopper can immediately undo the
 * jump if they didn't mean it (still just another entry on the stack).
 *
 * Props:
 *   - open, onClose      — controlled visibility
 *   - historyList        — [{ blocks, label, ts }, …] from useContentEditor
 *   - historyIndexNow    — current pointer into historyList
 *   - onJumpTo(index)    — jumps to that snapshot
 *
 * No external deps. Renders as a fixed panel — the parent is expected to be
 * the same z-order tree as EditDrawer.
 */
import React, { useMemo } from 'react';
import { X, Plus, Trash2, Pencil, ArrowUpDown, Clock, Circle, CheckCircle2 } from 'lucide-react';

function iconFor(label = '') {
  const l = label.toLowerCase();
  if (l.startsWith('add')) return Plus;
  if (l.startsWith('remove') || l.startsWith('delete')) return Trash2;
  if (l.startsWith('reorder')) return ArrowUpDown;
  return Pencil;
}

function relTime(ts) {
  if (!ts) return '';
  const diff = Math.max(0, Date.now() - ts);
  const s = Math.floor(diff / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function HistoryPanel({ open, onClose, historyList = [], historyIndexNow = -1, onJumpTo }) {
  // Newest first for the reader — but preserve original indexes so onJumpTo
  // uses the right pointer.
  const rows = useMemo(() => {
    return historyList
      .map((entry, idx) => ({ ...entry, idx }))
      .reverse();
  }, [historyList]);

  if (!open) return null;

  return (
    <div className="fixed inset-y-0 right-0 z-[150] w-[340px] max-w-[92vw] bg-[#0E0E23] text-white border-l border-white/10 shadow-2xl flex flex-col" data-testid="history-panel">
      <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-white/10">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/50">History</p>
          <h3 className="text-lg font-bold mt-0.5" style={{ fontFamily: 'Syne, sans-serif' }}>
            {historyList.length ? `${historyList.length} step${historyList.length === 1 ? '' : 's'}` : 'No steps yet'}
          </h3>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 text-white/60 hover:text-white rounded-md"
          data-testid="history-panel-close"
          aria-label="Close history panel"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="history-panel-list">
        {rows.length === 0 && (
          <div className="text-center py-12 text-white/40">
            <Clock size={24} className="mx-auto mb-2 opacity-60" />
            <p className="text-xs">Actions will show here as you edit.</p>
          </div>
        )}
        {rows.map(({ label, ts, idx }) => {
          const Icon = iconFor(label);
          const isActive = idx === historyIndexNow;
          const isFuture = idx > historyIndexNow;
          return (
            <button
              key={`${idx}-${ts}`}
              onClick={() => onJumpTo(idx)}
              className={
                'w-full text-left px-3 py-2.5 rounded-lg flex items-center gap-3 transition-colors ' +
                (isActive
                  ? 'bg-[#A78BFA]/20 border border-[#A78BFA]/40 text-white'
                  : isFuture
                    ? 'text-white/40 hover:bg-white/[0.04] hover:text-white/60'
                    : 'text-white/85 hover:bg-white/[0.05]')
              }
              data-testid={`history-item-${idx}`}
            >
              {isActive
                ? <CheckCircle2 size={14} className="shrink-0 text-[#A78BFA]" />
                : <Icon size={13} className="shrink-0 opacity-80" />}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{label || 'Edit'}</p>
                <p className="text-[10px] text-white/40 mt-0.5">{relTime(ts)}</p>
              </div>
              {isFuture && <Circle size={7} className="shrink-0 opacity-30" />}
            </button>
          );
        })}
      </div>

      <div className="shrink-0 px-5 py-3 border-t border-white/10 text-[10px] text-white/40 leading-relaxed">
        Jump to any step to preview it — you can always undo back to now.
        <br />Tip: <kbd className="px-1 py-0.5 rounded bg-white/10 text-white/60 font-mono text-[9px]">⌘Z</kbd> undo · <kbd className="px-1 py-0.5 rounded bg-white/10 text-white/60 font-mono text-[9px]">⌘⇧Z</kbd> redo
      </div>
    </div>
  );
}
